/**
 * Every skill's Edit-with-AI conversation, kept above the editor (#196).
 *
 * The editor used to hold its turn in its own state and abort it on the way out:
 * switching tab, closing the skill or opening another one stopped the edit
 * mid-run, and the agent filed nothing for an aborted turn. The chat had the same
 * shape until #193 moved its state into a store (`chatSessions.ts`); this is that
 * move for skill edits.
 *
 * One session per skill — the editor shows one conversation per skill at a time
 * (its current regen thread). A turn writes only into its own skill's session,
 * so it keeps running, with its own approval card and Stop, while the user is
 * anywhere else; reopening the skill shows it where it is.
 *
 * Plain state with a subscribe, not React, for the same reasons as the chat's.
 */
import type { ContextUsage, InterruptPayload } from "./chat";
import type { RegenThreadSummary } from "./skills";
import type { ThreadStatus } from "./threadStatus";
import type { ActivePlan, ChatMessage } from "../hooks/useChatTurn";

export interface CommitNotice {
  version: number;
  summary: string | null;
}

export interface SkillEditState {
  messages: ChatMessage[];
  /** An edit turn is in flight. */
  streaming: boolean;
  loadingHistory: boolean;
  interrupt: InterruptPayload | null;
  usage: ContextUsage | null;
  error: string | null;
  activePlan: ActivePlan | null;
  /** The Edit-with-AI history menu. */
  threads: RegenThreadSummary[];
  currentThreadId: string | null;
  /**
   * What the gate said about the current conversation (`lib/threadStatus.ts`).
   * Only AutoCAD skills meet a non-ready one in this stage.
   */
  threadStatus: ThreadStatus;
  /** Bumped whenever a commit or restore changed the version list. */
  versionsKey: number;
  /** The last commit, for the editor's toast — shown when the skill is next open. */
  commitNotice: CommitNotice | null;
}

export const EMPTY_SKILL_EDIT_STATE: SkillEditState = Object.freeze({
  messages: [],
  streaming: false,
  loadingHistory: false,
  interrupt: null,
  usage: null,
  error: null,
  activePlan: null,
  threads: [],
  currentThreadId: null,
  // `THREAD_READY`, spelled out: the store keeps no runtime imports.
  threadStatus: { kind: "ready" },
  versionsKey: 0,
  commitNotice: null,
}) as SkillEditState;

export interface SkillEditSession {
  skillId: string;
  /** The skill's display name, for the sidebar's Running list. */
  label: string;
  state: SkillEditState;
  /** The running turn's fetch; null when idle. */
  controller: AbortController | null;
  /**
   * The thread `stop` names to `POST /abort`. Set from `thread_info`: on the first
   * turn of a new thread the id arrives mid-stream.
   */
  liveThreadId: string | null;
  /** A live stream owns the thread — history must not reload over it. */
  ownsThread: boolean;
  /** Armed by "new conversation"; consumed by the next send. */
  newThread: boolean;
  /** The thread list and transcript were loaded once; reopening shows the session. */
  loaded: boolean;
  /** What the current turn was started with, kept for its resume. */
  pinned: {
    mode: string;
    effort: string | undefined;
    /** An AutoCAD cross-version build — its resume is one too. */
    crossVersion?: boolean;
  } | null;
}

export class SkillEditSessions {
  private readonly bySkill = new Map<string, SkillEditSession>();
  private readonly listeners = new Set<() => void>();
  private versionCounter = 0;

  /** Bumped on every change — the snapshot `useSyncExternalStore` compares. */
  get version(): number {
    return this.versionCounter;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The skill's session, if there is one. Never creates — safe during render. */
  peek(skillId: string): SkillEditSession | undefined {
    return this.bySkill.get(skillId);
  }

  /** The skill's session, created empty if there is none. */
  session(skillId: string): SkillEditSession {
    let session = this.bySkill.get(skillId);
    if (!session) {
      session = {
        skillId,
        label: "",
        state: EMPTY_SKILL_EDIT_STATE,
        controller: null,
        liveThreadId: null,
        ownsThread: false,
        newThread: false,
        loaded: false,
        pinned: null,
      };
      this.bySkill.set(skillId, session);
    }
    return session;
  }

  update(session: SkillEditSession, change: (state: SkillEditState) => SkillEditState): void {
    session.state = change(session.state);
    this.notify();
  }

  setController(session: SkillEditSession, controller: AbortController | null): void {
    session.controller = controller;
    this.notify();
  }

  /** Edits still working or waiting on an approval, oldest first — the sidebar's Running list. */
  active(): SkillEditSession[] {
    return [...this.bySkill.values()].filter(
      (session) => session.state.streaming || session.state.interrupt !== null,
    );
  }

  /** Stop every running edit — the shell is going away (sign-out, org gate). */
  abortAll(): void {
    for (const session of this.bySkill.values()) session.controller?.abort();
  }

  private notify(): void {
    this.versionCounter += 1;
    for (const listener of this.listeners) listener();
  }
}
